import React from 'react';
import { Wrench, UtensilsCrossed, Dumbbell, ShoppingBag, Heart, Rocket, ArrowRight, Check } from 'lucide-react';
import { industries } from '../../data/mock';
import { industryTranslations } from '../../data/translations';
import { useLanguage } from '../../context/LanguageContext';
import { AnimateOnScroll } from '../../hooks/useScrollAnimation';

const iconMap = {
  Wrench,
  UtensilsCrossed,
  Dumbbell,
  ShoppingBag,
  Heart,
  Rocket
};

const IndustriesSection = () => {
  const { t, language } = useLanguage();
  const translatedIndustries = industryTranslations[language] || industryTranslations.de;

  return (
    <section id="industries" className="py-24 lg:py-32 bg-gradient-to-b from-grey/5 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-mid-purple/5 rounded-full blur-hero" />
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-mid-blue/5 rounded-full blur-hero" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <AnimateOnScroll animation="fade-up">
            <span className="inline-block bg-gradient-to-r from-mid-orange/20 to-mid-yellow/20 text-dark-orange px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider mb-4 border border-mid-orange/20">
              {t('industries.badge')}
            </span>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.1}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-black dark:text-white mb-4">
              {t('industries.title')} <span className="gradient-text">{t('industries.titleHighlight')}</span> {t('industries.titleEnd')}
            </h2>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.2}>
            <p className="text-dark-grey dark:text-gray-400 text-lg max-w-3xl mx-auto">
              {t('industries.subtitle')}
            </p>
          </AnimateOnScroll>
        </div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((industry, index) => {
            const IconComponent = iconMap[industry.icon];
            const translated = translatedIndustries[index] || {};
            return (
              <AnimateOnScroll 
                key={industry.id} 
                animation="fade-up" 
                delay={0.1 * index}
              >
                <div className="group cursor-pointer h-full">
                  <div className="relative rounded-3xl overflow-hidden shadow-md card-hover bg-white dark:bg-gray-800 h-full">
                    {/* Image */}
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={industry.image}
                        alt={translated.title || industry.title}
                        className="w-full h-full object-cover img-zoom"
                      />
                      <div 
                        className="absolute inset-0 opacity-40"
                        style={{ backgroundColor: industry.bgColor }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      
                      {/* Icon Badge */}
                      <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                        {IconComponent && <IconComponent size={24} style={{ color: industry.bgColor }} />}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-black dark:text-white mb-2 flex items-center gap-2 group-hover:text-mid-purple transition-colors">
                        {translated.title || industry.title}
                        <ArrowRight size={18} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                      </h3>
                      <p className="text-dark-grey dark:text-gray-400 text-sm mb-4 leading-relaxed">
                        {translated.description || industry.description}
                      </p>
                      
                      {/* Features */}
                      <div className="flex flex-wrap gap-2">
                        {(translated.features || industry.features).map((feature, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-grey/10 dark:bg-gray-700 rounded-full text-xs text-dark-grey dark:text-gray-300"
                          >
                            <Check size={10} className="text-mid-green" />
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <AnimateOnScroll animation="fade-up" delay={0.3}>
          <div className="mt-16 text-center">
            <p className="text-dark-grey dark:text-gray-400 mb-6">
              {t('industries.notListed')} <span className="gradient-text font-medium">{t('industries.noProblem')}</span>
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-black dark:bg-white text-white dark:text-black px-8 py-4 rounded-full font-mono text-sm uppercase tracking-wider btn-lift shadow-lg"
            >
              {t('industries.cta')}
              <ArrowRight size={18} />
            </a>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default IndustriesSection;
